import { API, TV_API, unwrapData } from '../axios-instance'
import { Comment, ThreadType } from '../comment/types'
import { FlagCommentParams } from '../recognition'
import { handleBatchErrors } from '../shared/batch'
import {
  FederatedSearchRecipientsParams,
  FederatedSearchRecipientsResults,
  PageResult,
} from '../shared/types'
import {
  CreateCommentParams,
  CreateEndorsementParams,
  CreateReactionParams,
  FlagParams,
  GetCommentsParams,
  ListCommentsReactionsParams,
  ListEndorsementsParams,
  ListReactionsParams,
  UpdateWallPostCommentParams,
  UpdateWallPostMessageParams,
} from '../shared/wallPost/types'
import {
  Award,
  AwardEmbeds,
  AwardEndorsement,
  AwardReaction,
  AwardWithFlags,
  BatchAwardCreateParams,
  BatchAwardCreateResult,
  BatchAwardCsvTemplateVersion,
  BatchAwardExecuteParams,
  BatchAwardExecuteResult,
  BatchAwardPreviewParams,
  BatchAwardPreviewResult,
  BatchAwardResolveRowParams,
  BatchAwardRowsResult,
  BatchAwardWarningsResult,
  CreateAwardParams,
  GetAwardParams,
  ListAwardsParams,
} from './types'

export const createAward = async (params: CreateAwardParams, awardTypeId: string) => {
  return await API.post<Award>('/award/awards', { awardTypeId, ...params }).then(unwrapData)
}

// delegation
export const createDelegationAward = async (
  delegatorId: string,
  params: CreateAwardParams,
  awardTypeId: string,
) => {
  return await API.post<Award>(`/award/awards/delegator/${delegatorId}`, {
    awardTypeId,
    ...params,
  }).then(unwrapData)
}

export const getAward = async (awardId: string, params: GetAwardParams) => {
  return await API.get<Award>(`/award/awards/${awardId}`, { params }).then(unwrapData)
}

export const listAwardRecipients = async (params: FederatedSearchRecipientsParams) => {
  return await API.get<FederatedSearchRecipientsResults>('/award/recipients', { params }).then(
    unwrapData,
  )
}

export const listAwardSenders = async (params: FederatedSearchRecipientsParams) => {
  return await API.get<FederatedSearchRecipientsResults>('/award/senders', { params }).then(
    unwrapData,
  )
}

export const listAwards = async (params: ListAwardsParams) => {
  return await API.get<PageResult<Award>>('/award/awards', { params }).then(unwrapData)
}

export const endorseAward = async ({ amount, id }: CreateEndorsementParams) => {
  return await API.post<AwardEndorsement>(`/award/awards/${id}/endorsements`, {
    amount,
  }).then(unwrapData)
}

export const listAwardEndorsements = async (awardId: string, params: ListEndorsementsParams) => {
  return await API.get<PageResult<AwardEndorsement>>(`/award/awards/${awardId}/endorsements`, {
    params,
  }).then(unwrapData)
}

export const listAwardComments = async (id: string, params?: GetCommentsParams) => {
  return await API.get<PageResult<Comment>>(`/award/awards/${id}/comments`, {
    params,
  }).then(unwrapData)
}

export const createAwardComment = async ({
  body,
  id,
  tenantMemberId,
  threadId,
  gifs,
}: CreateCommentParams) => {
  return await API.post<Comment>(`/award/awards/${id}/comments`, {
    body,
    tenantMemberId,
    threadId,
    threadType: ThreadType.AWARD,
    gifs: gifs || [],
  }).then(unwrapData)
}

export const listAwardCommentReactions = async (
  commentId: string,
  id: string,
  params: ListCommentsReactionsParams,
) => {
  return await API.get<PageResult<AwardReaction>>(
    `/award/awards/${id}/comments/${commentId}/reactions`,
    { params },
  ).then(unwrapData)
}

export const reactToAward = async ({ reaction, postId }: CreateReactionParams) => {
  return await API.put<AwardReaction>(`/award/awards/${postId}/reactions`, {
    reaction,
    awardId: postId,
  }).then(unwrapData)
}

export const removeAwardReaction = async ({ postId }: Pick<CreateReactionParams, 'postId'>) => {
  return await API.delete<boolean>(`/award/awards/${postId}/reactions`).then(unwrapData)
}

export const listAwardReactions = async (postId: string, params: ListReactionsParams) => {
  return await API.get<PageResult<AwardReaction>>(`/award/awards/${postId}/reactions`, {
    params,
  }).then(unwrapData)
}

export const reactToAwardComment = async ({
  reaction,
  postId,
  commentId,
}: CreateReactionParams) => {
  return await API.put<AwardReaction>(`/award/awards/${postId}/comments/${commentId}/reactions`, {
    reaction,
    awardId: postId,
    commentId,
  }).then(unwrapData)
}

export const removeAwardCommentReaction = async ({
  postId,
  commentId,
}: Pick<CreateReactionParams, 'postId' | 'commentId'>) => {
  return await API.delete<boolean>(`/award/awards/${postId}/comments/${commentId}/reactions`).then(
    unwrapData,
  )
}

export const flagAward = async (id: string) => {
  return await API.put<boolean>(`/award/awards/${id}/flags`).then(unwrapData)
}

export const restoreFlaggedAward = async ({ postId }: FlagParams) => {
  return await API.delete<boolean>(`/award/awards/${postId}/flags`)
}

export const listFlaggedAwards = async (params?: ListAwardsParams) => {
  return await API.get<PageResult<AwardWithFlags>>('/award/awards/flagged', {
    params,
  }).then(unwrapData)
}

export const flagAwardComment = async ({ id, commentId }: FlagCommentParams) => {
  return await API.put<boolean>(`/award/awards/${id}/comments/${commentId}/flags`).then(unwrapData)
}

export const editAwardMessage = async (params: UpdateWallPostMessageParams, awardId: string) => {
  return await API.patch<Award>(`/award/awards/${awardId}`, params).then(unwrapData)
}

export const listTvAwards = async (token: string, embeds?: AwardEmbeds[]) => {
  return await TV_API.get<Award[]>(`/award/tv/${token}/awards`, {
    params: { embeds },
  }).then(unwrapData)
}

export const editAwardComment = async (
  params: UpdateWallPostCommentParams,
  awardId: string,
  commentId: string,
) => {
  return await API.patch<Comment>(`/award/awards/${awardId}/comments/${commentId}`, params).then(
    unwrapData,
  )
}

export const downloadBatchAwardCsvTemplate = async (version: BatchAwardCsvTemplateVersion) => {
  return await API.get<BlobPart>('/award/awards/template', {
    params: { version },
    responseType: 'blob',
  }).then(unwrapData)
}

// batch award
const batchAwardBaseUrl = '/award/awards/batchAwardV2'

export const createBatchAwardSendBatch = async ({
  file,
  awardTypeId,
  delegatorId,
}: BatchAwardCreateParams) => {
  return await API.post<BatchAwardCreateResult>(
    batchAwardBaseUrl,
    {
      file,
      awardTypeId,
      delegatorId: delegatorId ?? null,
    },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  ).then(unwrapData)
}

export const getBatchAwardRows = async (batchId: string) => {
  return await API.get<BatchAwardRowsResult>(`${batchAwardBaseUrl}/${batchId}/rows`, {
    skip404Interceptor: true,
  })
    .then(unwrapData)
    .catch(handleBatchErrors)
}

export const resolveBatchAwardRow = async (
  batchId: string,
  row: number,
  params: BatchAwardResolveRowParams,
) => {
  return await API.put<void>(`${batchAwardBaseUrl}/${batchId}/rows/${row}`, params)
    .then(unwrapData)
    .catch(handleBatchErrors)
}

export const getBatchAwardPreview = async (batchId: string, params?: BatchAwardPreviewParams) => {
  return await API.get<BatchAwardPreviewResult>(`${batchAwardBaseUrl}/${batchId}`, {
    params,
    skip404Interceptor: true,
  })
    .then(unwrapData)
    .catch(handleBatchErrors)
}

export const executeBatchAward = async (batchId: string, params: BatchAwardExecuteParams) => {
  return await API.post<BatchAwardExecuteResult>(`${batchAwardBaseUrl}/${batchId}`, params, {
    skip404Interceptor: true,
  })
    .then(unwrapData)
    .catch(handleBatchErrors)
}

export const getBatchAwardWarnings = async (batchId: string) => {
  return await API.get<BatchAwardWarningsResult>(`${batchAwardBaseUrl}/${batchId}/warnings`, {
    skip404Interceptor: true,
  })
    .then(unwrapData)
    .catch(handleBatchErrors)
}
